/**
 * SharkPreview — the turntable behind the landing page.
 *
 * A chooser you cannot see is a form. This puts the animal itself on screen while its dials are
 * being moved, in the same shader it will be lit by in the water, so what you pick is what you
 * get rather than an approximation of it.
 *
 * IT BORROWS THE GAME'S RENDERER. One WebGL context, one canvas: the landing draws into the
 * same surface the reef will, and simply stops being drawn when the game starts. A second
 * canvas would mean a second context — a real cost on integrated graphics, and browsers cap how
 * many a page may hold — for a picture that is on screen for twenty seconds.
 *
 * The camera frames by LENGTH rather than sitting at a fixed distance, because the roster runs
 * from a 3.7 u mako to an 18.5 u whale shark. At a fixed distance the small ones are specks and
 * the big ones run off both edges.
 */
import * as THREE from 'three';
import { SharkMaterial, swimParamsForSpeed } from './SharkMaterial.ts';
import { DayNight } from '../world/DayNight.ts';
import { MOVEMENT } from '../data/tuning.ts';
import type { SharkMesh } from '../voxel/MeshService.ts';

/** phase to light the preview at: mid-morning, warm and raking, good on a countershaded flank */
const PREVIEW_PHASE = 0.36;
/** how much longer than the shark the camera stands off; tuned so a whale shark still fits */
const FRAME = 1.34;
/** how far in and out a scroll may take it, as a multiple of the framed distance */
const ZOOM_MIN = 0.45;
const ZOOM_MAX = 3.0;
/** stop just short of the poles: at exactly +-90 degrees the up vector is undefined and the
    view snaps through a right angle for one frame */
const PITCH_LIMIT = 1.45;
/** turntable rate, rad/s. Slow enough to read the markings, fast enough to feel alive. */
const SPIN = 0.42;
/**
 * How wide the animal projects at FRAME with no correction, in NDC (2 = the whole screen),
 * AT REF_ASPECT.
 *
 * Measured, not derived: the framing distance scales with the animal, so every shark in the
 * roster lands at the same on-screen size and one number covers all eight.
 *
 * The aspect qualifier is the part that matters and the part I first left out. The frustum's
 * half-width is d*tan(fov/2)*ASPECT, so the same animal fills more of a 4:3 screen than a 16:9
 * one — projected width scales as 1/aspect. Treating this as a constant made the correction
 * right at the aspect it was measured at and wrong everywhere else, which is exactly how it
 * failed: fine at 1920x1080 and overlapping the panel by 1024x768.
 */
const NDC_WIDTH = 1.54;
/**
 * And how tall, in NDC. Aspect does not enter — the frustum's half height is d*tan(fov/2) with
 * no aspect term in it.
 *
 * MEASURED, and the measurement mattered: I first wrote 0.74 from intuition, on the reasoning
 * that a shark is a long flat shape and must project short. It is 1.75. Seen from three
 * quarters above, a body length lying away from the camera covers nearly the whole frame
 * vertically, and the guess would have under-corrected by a factor of two — cropping the animal
 * top and bottom on every phone.
 */
const NDC_HEIGHT = 1.75;
const REF_ASPECT = 16 / 9;

// Scratch. update() runs every frame and must not allocate.
const EYE = new THREE.Vector3();
const AIM = new THREE.Vector3();
const RIGHT = new THREE.Vector3();
const UP = new THREE.Vector3(0, 1, 0);
const LOCAL_UP = new THREE.Vector3();
const FWD = new THREE.Vector3();

export class SharkPreview {
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;

  private readonly root = new THREE.Group();
  private readonly material = new SharkMaterial({ jaw: true });
  private mesh: THREE.Mesh | null = null;
  private length = 8;
  private spin = 0;
  /** where the animal sits across the frame: 0 centred, +1 hard right */
  private bias = 0;
  /** and up the frame: 0 centred, +1 hard top */
  private biasY = 0;
  /** extra stand-off so the animal fits the space the panel leaves; 1 is the default framing */
  private fit = 1;
  private aspect = 1.6;

  /* Orbit. The turntable keeps spinning until the first drag and then hands over for good:
     a view that creeps back to spinning under the cursor feels like the page arguing. */
  private yaw = 0;
  private pitch = 0.26;
  private zoom = 1;
  private readonly pan = new THREE.Vector2();
  private grabbed = false;
  private touched = false;
  private el: HTMLElement | null = null;
  private pointer = -1;
  private panning = false;
  private lastX = 0;
  private lastY = 0;
  /* Second finger, for pinch. Tracked separately rather than through a list because there are
     exactly two gestures here and a general multi-touch manager for two gestures is a way of
     writing more code than the problem has. */
  private pinchId = -1;
  private pinchX = 0;
  private pinchY = 0;
  private pinchStart = 0;
  private zoomAtPinch = 1;
  private readonly sky = new DayNight();

  constructor() {
    this.camera = new THREE.PerspectiveCamera(34, 1, 0.1, 400);
    this.scene.add(this.root);

    /* Lit by the same day cycle as the reef, frozen at one phase. Reusing it rather than
       inventing preview lighting means a shark cannot look one way here and another in the
       water — which is the entire promise a chooser makes. */
    this.material.setEnv(this.sky.sample(PREVIEW_PHASE));
    // No shadow map on this pass: one caster with nothing to cast onto.
    this.material.setShadow(null, new THREE.Matrix4(), 0);

    /* A backdrop, so the animal is not floating in the void. Same gradient trick the water
       volume uses — an inward-facing sphere — because the landing sits over the same canvas
       and an empty clear colour reads as a broken page rather than as deep water. */
    const backdrop = new THREE.Mesh(
      new THREE.SphereGeometry(180, 20, 12),
      new THREE.ShaderMaterial({
        name: 'SharkPreview:backdrop',
        vertexShader: `
          varying float vY;
          void main() {
            vec4 w = modelMatrix * vec4(position, 1.0);
            vY = w.y;
            gl_Position = projectionMatrix * viewMatrix * w;
          }
        `,
        fragmentShader: `
          uniform vec3 uTop;
          uniform vec3 uBottom;
          varying float vY;
          void main() {
            gl_FragColor = vec4(mix(uBottom, uTop, smoothstep(-90.0, 60.0, vY)), 1.0);
          }
        `,
        uniforms: {
          uTop: { value: new THREE.Vector3(0.045, 0.170, 0.205) },
          uBottom: { value: new THREE.Vector3(0.006, 0.028, 0.048) },
        },
        side: THREE.BackSide,
        depthWrite: false,
        fog: false,
      }),
    );
    backdrop.renderOrder = -1;
    backdrop.frustumCulled = false;
    this.scene.add(backdrop);
  }

  /**
   * Adopt a freshly built shark. The previous geometry is disposed here rather than by the
   * caller: a chooser rebuilds on every slider move, and a leak of one shark per drag is a
   * hundred megabytes inside a minute.
   */
  setMesh(built: SharkMesh): void {
    if (this.mesh) {
      this.root.remove(this.mesh);
      this.mesh.geometry.dispose();
    }
    this.material.setSpineLength(built.size.z);
    this.material.setJawRig(built.jaw);
    const m = new THREE.Mesh(built.geometry, this.material);
    m.frustumCulled = false;
    this.root.add(m);
    this.mesh = m;
    this.length = Math.max(1, built.size.z);
  }

  get hasMesh(): boolean {
    return this.mesh !== null;
  }

  /**
   * Take pointer input from the canvas.
   *
   * The canvas and not the overlay: the landing panel sits on top and must keep its own
   * clicks, so it is the only part of the overlay that takes pointer events at all. Everything
   * outside it falls through to here, which is what makes the empty two thirds of the screen a
   * viewport you can grab rather than a dead area.
   */
  attach(el: HTMLElement): void {
    this.el = el;
    el.addEventListener('pointerdown', this.onDown);
    el.addEventListener('pointermove', this.onMove);
    el.addEventListener('pointerup', this.onUp);
    el.addEventListener('pointercancel', this.onUp);
    el.addEventListener('wheel', this.onWheel, { passive: false });
    el.addEventListener('contextmenu', this.onContext);
  }

  private onDown = (e: PointerEvent): void => {
    if (this.pointer !== -1 && this.pinchId === -1 && e.pointerType === 'touch') {
      // A second finger turns the drag into a pinch rather than being ignored.
      this.pinchId = e.pointerId;
      this.pinchX = e.clientX;
      this.pinchY = e.clientY;
      this.pinchStart = Math.hypot(e.clientX - this.lastX, e.clientY - this.lastY);
      this.zoomAtPinch = this.zoom;
      this.el?.setPointerCapture(e.pointerId);
      return;
    }
    if (this.pointer !== -1) return;
    this.pointer = e.pointerId;
    this.grabbed = true;
    this.touched = true;
    // Right button, middle button, or shift: pan. Anything else orbits.
    this.panning = e.button === 1 || e.button === 2 || e.shiftKey;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    this.el?.setPointerCapture(e.pointerId);
  };

  private onMove = (e: PointerEvent): void => {
    if (e.pointerId === this.pinchId) {
      this.pinchX = e.clientX;
      this.pinchY = e.clientY;
      const span = Math.hypot(this.pinchX - this.lastX, this.pinchY - this.lastY);
      if (this.pinchStart > 8 && span > 8) {
        this.zoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, this.zoomAtPinch * (this.pinchStart / span)));
      }
      return;
    }
    if (!this.grabbed || e.pointerId !== this.pointer) return;
    // While pinching, the first finger sets the span and must not also orbit.
    if (this.pinchId !== -1) {
      this.lastX = e.clientX;
      this.lastY = e.clientY;
      return;
    }
    const dx = e.clientX - this.lastX;
    const dy = e.clientY - this.lastY;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    if (this.panning) {
      // Scaled by the animal, so dragging feels the same on a mako and a whale shark.
      const k = this.length * 0.0022 * this.zoom;
      this.pan.x -= dx * k;
      this.pan.y += dy * k;
    } else {
      this.yaw -= dx * 0.008;
      this.pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, this.pitch + dy * 0.006));
    }
  };

  private onUp = (e: PointerEvent): void => {
    if (e.pointerId === this.pinchId) {
      this.pinchId = -1;
      return;
    }
    if (e.pointerId !== this.pointer) return;
    this.grabbed = false;
    this.pointer = -1;
    if (this.el?.hasPointerCapture(e.pointerId)) this.el.releasePointerCapture(e.pointerId);
  };

  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    this.touched = true;
    // Multiplicative, so one notch is the same proportion of the distance at every distance.
    this.zoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, this.zoom * Math.exp(e.deltaY * 0.0011)));
  };

  private onContext = (e: Event): void => {
    // Right-drag is pan; without this the browser menu opens on top of it.
    e.preventDefault();
  };

  /** put the view back where it started */
  reset(): void {
    this.yaw = 0;
    this.pitch = 0.26;
    this.zoom = 1;
    this.pan.set(0, 0);
    this.touched = false;
  }

  update(dt: number, time: number): void {
    if (!this.touched) this.spin += dt * SPIN;
    this.root.rotation.y = 0;

    // Swimming on the spot at cruise, so the tail beat and the body wave are both visible —
    // a motionless shark reads as a model and a moving one reads as an animal.
    const swim = swimParamsForSpeed(MOVEMENT.cruiseSpeed, MOVEMENT.cruiseSpeed);
    this.material.setSwim(swim.amp, swim.speed);
    this.material.setCurve(Math.sin(time * 0.5) * 0.10);
    this.material.setTime(time);

    /* Framed from a three-quarter view rather than side-on. A pure profile is the most
       informative angle and the least attractive one; this keeps the head reading as a head
       while still showing the whole flank.

       The idle spin and the player's own yaw are the SAME angle rather than a spinning model
       inside an orbiting camera: two rotations compose into something that cannot be reasoned
       about, and dragging left would stop meaning turn left. */
    const d = this.length * FRAME * this.fit * this.zoom;
    const yaw = this.yaw + this.spin + 0.9;
    const cp = Math.cos(this.pitch);
    AIM.set(0, 0, 0);
    EYE.set(Math.sin(yaw) * cp * d, Math.sin(this.pitch) * d, Math.cos(yaw) * cp * d);

    /* The camera's own basis, and it has to be built FORWARD.
       cross(EYE - AIM, up) points along screen-LEFT, not screen-right — EYE-AIM aims backwards
       out of the lens. Using it as "right" silently inverts everything derived from it, which
       is what put the shark on the left of the screen while the code asked for the right and
       made a rightward pan drag push the animal away from the cursor. */
    FWD.subVectors(AIM, EYE).normalize();
    RIGHT.crossVectors(FWD, UP).normalize();
    LOCAL_UP.crossVectors(RIGHT, FWD).normalize();

    /* Pan along those axes rather than the world's: offsetting in world x and y means that once
       you have orbited round the animal, dragging right moves it toward you instead of across
       the screen, and the control stops matching the hand doing it.

       The camera moves OPPOSITE the drag so the shark travels with the cursor, which is what
       grabbing something means. */
    AIM.addScaledVector(RIGHT, this.pan.x).addScaledVector(LOCAL_UP, this.pan.y);
    EYE.addScaledVector(RIGHT, this.pan.x).addScaledVector(LOCAL_UP, this.pan.y);

    /* Slide the whole rig sideways rather than turning it. Panning the camera off the subject
       would foreshorten the far flank; moving eye and target together keeps the shark square to
       the lens and simply puts it elsewhere on screen — beside the panel instead of behind it.
       Camera LEFT to put the subject right, and derived from the frustum so it means the same
       fraction of the frame whatever the window is doing. */
    const halfH = d * Math.tan((this.camera.fov * Math.PI) / 360);
    if (this.bias !== 0) {
      const halfW = halfH * this.aspect;
      EYE.addScaledVector(RIGHT, -this.bias * halfW);
      AIM.addScaledVector(RIGHT, -this.bias * halfW);
    }
    if (this.biasY !== 0) {
      // Camera DOWN to put the subject up, same inversion as the horizontal case.
      EYE.addScaledVector(LOCAL_UP, -this.biasY * halfH);
      AIM.addScaledVector(LOCAL_UP, -this.biasY * halfH);
    }

    this.camera.position.copy(EYE);
    this.camera.lookAt(AIM);
  }

  /**
   * Fit the animal into whatever the panel has left, measured rather than guessed.
   *
   * A fixed bias cannot work: the panel is a fixed 400px, so it takes a quarter of a 1920
   * viewport and half of a 1024 one, and any single offset that clears it on one is wrong on
   * the other. Worse, the shark is framed to fill the screen, so at 1920 it was 1472px wide
   * against 1424px of free space — it could not have fitted no matter where it was put.
   *
   * So both numbers come from the space that actually exists: shift to the centre of it, and
   * stand off far enough that the animal fits inside it. Below a third of the width there is
   * no point pretending, and it goes back to centre behind a panel that is backed anyway.
   *
   * @param leftFraction where the panel's right edge falls, 0..1 across the viewport
   */
  setSafeArea(leftFraction: number, topFraction = 0): void {
    const freeRight = 1 - Math.max(0, Math.min(1, leftFraction));
    const freeTop = Math.max(0, Math.min(1, topFraction));

    /* Two ways to get out of the panel's way, and which one applies is a property of the
       LAYOUT rather than of the device. On a desktop the panel is a column down the left and
       the room is beside it; on a phone it is a sheet across the bottom and the room is above
       it. Sliding sideways into 30% of a phone's width would shrink the animal to a sliver to
       satisfy a rule about not overlapping, which is not what the rule is for. */
    if (freeRight >= 0.44) {
      this.bias = 2 * (leftFraction + freeRight / 2) - 1;
      this.biasY = 0;
      const wide = NDC_WIDTH * (REF_ASPECT / Math.max(0.5, this.aspect));
      this.fit = Math.max(1, wide / (2 * freeRight * 0.92));
      return;
    }

    if (freeTop >= 0.30) {
      this.bias = 0;
      // NDC y is +1 at the top, and the free strip runs from the top down to topFraction.
      this.biasY = 1 - freeTop;
      /* Constrained by BOTH axes: the strip is full width but short, so the animal has to fit
         its height as well — and it is a long shape seen from three quarters above, so height
         is usually the binding one. */
      const wide = NDC_WIDTH * (REF_ASPECT / Math.max(0.5, this.aspect));
      this.fit = Math.max(
        1,
        wide / (2 * 0.94),
        NDC_HEIGHT / (2 * freeTop * 0.88),
      );
      return;
    }

    this.bias = 0;
    this.biasY = 0;
    this.fit = 1;
  }

  resize(width: number, height: number): void {
    this.aspect = width / Math.max(1, height);
    this.camera.aspect = this.aspect;
    this.camera.updateProjectionMatrix();
  }

  dispose(): void {
    const el = this.el;
    if (el) {
      el.removeEventListener('pointerdown', this.onDown);
      el.removeEventListener('pointermove', this.onMove);
      el.removeEventListener('pointerup', this.onUp);
      el.removeEventListener('pointercancel', this.onUp);
      el.removeEventListener('wheel', this.onWheel);
      el.removeEventListener('contextmenu', this.onContext);
      this.el = null;
    }
    if (this.mesh) this.mesh.geometry.dispose();
    this.material.dispose();
  }
}
